/**
 * Shared supplier name matching utility.
 *
 * Normalises company names by removing common suffixes (Pty, Ltd, etc.),
 * honorifics, and non-alphanumeric characters, then checks for exact or
 * substring matches. Used by the capture form and DocumentModal to detect
 * when the AI-detected vendor name differs from the technician's selected
 * supplier.
 */

/**
 * Check whether two supplier/company names match after normalisation.
 * Returns true if:
 *  - Either name is empty (can't compare — don't flag)
 *  - The normalised forms are identical
 *  - One normalised form contains the other (e.g. "mrpricesport" vs "mr price sport")
 */
/**
 * Entry types (and raw AI document_type strings that map to them) that imply
 * "this document came from a known supplier". Used to gate the
 * category-variance flag: an AI "supplier invoice" detection is only a
 * meaningful disagreement when the extracted vendor actually cross-references
 * a supplier in the client's contact list — otherwise a slip printed "TAX
 * INVOICE" would flag every general expense for review.
 */
const SUPPLIER_DOCUMENT_ENTRY_TYPES = new Set([
  'supplier_invoice',
  'supplier_quote',
  'delivery_note',
  'grn',
  'purchase_return',
  'supplier_credit',
  // Raw document_type values that mapAiDocumentTypeToEntryType maps to
  // supplier-document entry types.
  'tax_invoice',
  'invoice',
  'statement',
  'credit_note',
]);

/** True when the given entry type / AI document type denotes a supplier document. */
export function isSupplierDocumentEntryType(type: unknown): boolean {
  if (typeof type !== 'string' || !type.trim()) return false;
  return SUPPLIER_DOCUMENT_ENTRY_TYPES.has(type.trim().toLowerCase().replace(/[\s-]+/g, '_'));
}

export function supplierNamesMatch(nameA: string, nameB: string): boolean {
  const normalize = (s: string) =>
    s.toLowerCase()
      .replace(/\b(ltd|pty|limited|inc|corp|corporation|co|cc|holdings|group|enterprises)\b\.?/g, '')
      .replace(/\b(mr|mrs|ms|dr)\b\.?/g, '')
      .replace(/[^a-z0-9]/g, '')
      .trim();
  const a = normalize(nameA);
  const b = normalize(nameB);
  if (!a || !b) return true; // Can't compare — don't flag
  if (a === b) return true;
  if (a.includes(b) || b.includes(a)) return true;
  return false;
}
